// SPDX-License-Identifier: Elastic-2.0
/**
 * MirlaiMark — the bird-mountain silhouette logo.
 *
 * Path data copied verbatim from
 * `mirlai-os/design-system/assets/mirlai-mark.svg` (same source as the
 * `MirlaiMarkOS` / `DMark` JSX components). Native viewBox is
 * 521.112 × 407.258, so the rendered height is `size * (407.258 / 521.112)`
 * to preserve aspect ratio at any width.
 *
 * `color` maps to the SVG `fill`. Defaults to `currentColor` so the mark
 * inherits the text colour of its container — the typical onboarding
 * + dashboard pattern is to render it inside a header that already
 * carries the right foreground token.
 */

const NATIVE_W = 521.112;
const NATIVE_H = 407.258;

const PATH_D =
  "M 137.786 0.585 C 194.579 -7.178 223.321 64.908 259.886 65.67 C 295.161 66.406 320.449 3.331 372.103 0.412 C 386.192 -0.385 399.724 2.742 412.16 9.392 C 425.184 16.352 436.719 28.433 447.82 38.17 L 491.802 76.93 C 515.792 98.398 521.634 118.949 521.046 150.776 C 520.501 180.417 521.695 210.578 520.703 240.222 C 520.17 256.156 497.331 260.872 491.184 245.82 C 489.114 240.515 489.88 227.797 489.898 221.732 L 489.972 161.531 C 489.978 141.596 491.809 119.44 475.638 104.243 C 457.342 87.047 437.779 71.003 419.159 54.128 C 414.242 49.672 408.449 44.315 403.196 40.357 C 400.214 38.127 396.981 36.258 393.558 34.792 C 339.167 11.893 316.983 107.726 248.644 95.62 C 206.866 88.22 180.53 31.762 144.768 31.533 C 137.473 31.486 129.155 33.244 122.796 36.912 C 112.784 42.689 96.33 58.815 86.683 67.255 C 72.771 79.428 57.857 91.186 44.851 104.274 C 41.42 107.726 37.584 112.38 35.615 116.839 C 28.247 133.524 30.825 170.679 30.785 190.09 C 56.198 191.685 87.21 189.839 110.896 195.28 C 142.42 202.522 172.754 226.595 189.109 254.054 C 195.545 264.958 201.266 277.124 208.175 287.662 C 236.114 330.285 281.745 359.141 333.711 356.101 C 345.853 355.391 361.161 356.112 373.689 356.108 L 458.756 356.056 C 472.025 356.011 485.802 355.53 498.979 356.418 C 510.239 357.176 519.227 366.897 520.666 377.808 C 521.591 384.911 519.607 392.081 515.174 397.708 C 512.284 401.358 506.822 405.687 502.058 406.238 C 490.308 407.597 478.038 407.205 466.165 407.205 L 417.162 407.193 L 254.559 407.193 L 119.407 407.199 L 77.674 407.217 C 56.088 407.224 41.95 408.405 23.918 394.059 C 10.899 383.827 2.511 368.817 0.618 352.367 C -0.388 343.183 0.135 323.414 0.143 313.526 L 0.19 240.426 L 0.114 170.621 C 0.057 136.539 -2.817 104.932 25.301 79.881 C 35.316 70.959 45.503 62.148 55.606 53.329 L 83.785 28.761 C 102.185 12.744 112.394 3.379 137.786 0.585 Z";

export interface MirlaiMarkProps {
  /**
   * Rendered width in pixels. Height is derived to preserve the native
   * 521.112×407.258 aspect ratio. Defaults to 36 — the WizardShell norm.
   */
  size?: number;
  /**
   * Fill colour. Defaults to `currentColor` so the mark inherits its
   * container's text colour and themes automatically.
   */
  color?: string;
  /**
   * Optional accessible label. When omitted (the default), the SVG is
   * marked `aria-hidden` — useful when the mark sits next to a wordmark
   * that already conveys the brand name.
   */
  title?: string;
  /** Optional class for layout/positioning overrides. */
  className?: string;
}

export function MirlaiMark({
  size = 36,
  color = "currentColor",
  title,
  className,
}: MirlaiMarkProps): JSX.Element {
  const height = size * (NATIVE_H / NATIVE_W);
  const decorative = title === undefined;
  return (
    <svg
      width={size}
      height={height}
      viewBox={`0 0 ${NATIVE_W} ${NATIVE_H}`}
      fill="none"
      role={decorative ? undefined : "img"}
      aria-hidden={decorative || undefined}
      aria-label={decorative ? undefined : title}
      className={className}
      data-testid="mirlai-mark"
      style={{ display: "block" }}
    >
      {title ? <title>{title}</title> : null}
      <path d={PATH_D} fill={color} fillRule="nonzero" />
    </svg>
  );
}
